import React, { useCallback, useEffect, useMemo, useState } from "react";
import { notification, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardHeader from "components/Dashboard/Header";
import DashboardPageChrome from "components/Dashboard/DashboardPageChrome";
import NestedTabs from "components/NestedTabs";
import { apiRequest } from "util/services";
import * as actions from "redux/Dashboard/actions.js";
import styles from "./style.module.scss";

const SUPPORTS = ["1", "2", "3", "4", "5plus"];
const PAGE_SIZE = 10;

const csvEscape = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const triggerCsvDownload = (filename, text) => {
  const blob = new Blob(["\uFEFF" + text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const FEEDBACK_OPTIONS = [
  { id: "not_a_concept", key: "topic_gap_fb_not_concept" },
  { id: "real_gap", key: "topic_gap_fb_real_gap" },
  { id: "already_covered", key: "topic_gap_fb_already_covered" },
  { id: "bad_normalize", key: "topic_gap_fb_bad_normalize" },
];

const whyText = (reason, t) => {
  if (!reason || reason === "no_positive_containment") {
    return t("dashboard.topic_gap_unresolved", {
      defaultValue:
        "Neden yeni: taksonomi adında birebir kapsama yok (UNRESOLVED).",
    });
  }
  return reason;
};

const fmtDay = (iso) => {
  if (!iso) return "—";
  const d = String(iso).slice(0, 10);
  return d;
};

const TopicGapConcepts = ({ match, shared, user }) => {
  const { id } = useParams();
  const dashboardId = Number(id);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const DashboardReducer = useSelector((state) => state.DashboardReducer);
  const reduxUser = DashboardReducer.user;

  const [support, setSupport] = useState("5plus");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [drawer, setDrawer] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [feedbackLabel, setFeedbackLabel] = useState("not_a_concept");
  const [feedbackReason, setFeedbackReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!id) return;
    apiRequest("dashboards/v2/get", "POST", { dashboard_id: Number(id) }, true).then(
      (result) => {
        if (result && !result.error) {
          dispatch({
            type: actions.SET_Dashboard,
            payload: { ...result, loading: false },
          });
        } else {
          notification.error({
            message: result?.error || "Unable to get dashboard info",
          });
        }
      }
    );
  }, [id, dispatch]);

  const load = useCallback(() => {
    if (!dashboardId) return;
    setLoading(true);
    apiRequest(
      `topic-gap/${dashboardId}/concepts?support=${support}&page=${page}&page_size=${PAGE_SIZE}`,
      "GET",
      null,
      true
    )
      .then((result) => {
        if (result && !result.error) {
          setPayload(result);
        } else {
          notification.error({
            message:
              result?.error ||
              t("dashboard.topic_gap_load_error", {
                defaultValue: "Konsept listesi yüklenemedi",
              }),
          });
        }
      })
      .finally(() => setLoading(false));
  }, [dashboardId, support, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelected(new Set());
  }, [support, page]);

  const concepts = payload?.concepts || [];
  const histogram = payload?.histogram || {};
  const total = payload?.total || 0;
  const filtered = payload?.filtered || 0;
  const pageCount = Math.max(1, Math.ceil(filtered / PAGE_SIZE));

  const openDrawer = (concept) => {
    setDrawer(concept);
    setFeedbackLabel("not_a_concept");
    setFeedbackReason("");
    setReviews([]);
    setReviewsLoading(true);
    apiRequest(
      `topic-gap/${dashboardId}/concepts/${concept.concept_id}/reviews`,
      "GET",
      null,
      true
    )
      .then((result) => {
        if (result && !result.error) {
          setReviews(result.reviews || []);
        }
      })
      .finally(() => setReviewsLoading(false));
  };

  const toggle = (conceptId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(conceptId)) next.delete(conceptId);
      else next.add(conceptId);
      return next;
    });
  };

  const downloadCsv = async () => {
    if (!dashboardId || downloading) return;
    setDownloading(true);
    try {
      const result = await apiRequest(
        `topic-gap/${dashboardId}/concepts/export?support=${support}`,
        "GET",
        null,
        true
      );
      if (!result || result.error) {
        notification.error({
          message:
            result?.error ||
            t("dashboard.topic_gap_download_error", {
              defaultValue: "CSV indirilemedi",
            }),
        });
        return;
      }
      const rows = result.concepts || [];
      const headers = [
        t("dashboard.topic_gap_col_concept", { defaultValue: "Konsept" }),
        t("dashboard.topic_gap_col_why", { defaultValue: "Neden yeni" }),
        t("dashboard.topic_gap_col_reviews", { defaultValue: "Yorum" }),
        t("dashboard.topic_gap_days", { defaultValue: "Takvim günü" }),
        t("dashboard.topic_gap_col_discovery", { defaultValue: "Keşif" }),
        t("dashboard.topic_gap_col_updated", { defaultValue: "Son güncelleme" }),
        "coverage_state",
      ];
      const csv = [
        headers.map(csvEscape).join(","),
        ...rows.map((row) =>
          [
            row.canonical_form,
            whyText(row.coverage_reason, t),
            row.unique_review_count,
            row.calendar_day_count,
            fmtDay(row.first_seen_run_at || row.first_seen_at),
            fmtDay(row.last_processed_run_at || row.last_seen_at),
            row.coverage_state,
          ]
            .map(csvEscape)
            .join(",")
        ),
      ].join("\n");
      triggerCsvDownload(`topic-gap-${dashboardId}-${support}.csv`, csv);
      if (result.truncated) {
        notification.warning({
          message: t("dashboard.topic_gap_download_truncated", {
            defaultValue: "İndirme üst sınıra takıldı; tüm satırlar yok.",
          }),
        });
      } else {
        notification.success({
          message: t("dashboard.topic_gap_download_ok", {
            defaultValue: "{{count}} konsept indirildi",
            count: rows.length,
          }),
        });
      }
    } finally {
      setDownloading(false);
    }
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    setSaving(true);
    const result = await apiRequest(
      `topic-gap/${dashboardId}/concepts/delete`,
      "POST",
      { concept_ids: Array.from(selected) },
      true
    );
    setSaving(false);
    if (result && !result.error) {
      notification.success({
        message: t("dashboard.topic_gap_deleted", {
          defaultValue: "Seçilen konseptler silindi",
        }),
      });
      setSelected(new Set());
      if (drawer && selected.has(drawer.concept_id)) setDrawer(null);
      load();
    } else {
      notification.error({
        message: result?.error || "Delete failed",
      });
    }
  };

  const saveFeedback = async () => {
    if (!drawer) return;
    setSaving(true);
    const result = await apiRequest(
      `topic-gap/${dashboardId}/concepts/${drawer.concept_id}/feedback`,
      "POST",
      { label: feedbackLabel, reason: feedbackReason },
      true
    );
    setSaving(false);
    if (result && !result.error) {
      notification.success({
        message: t("dashboard.topic_gap_feedback_saved", {
          defaultValue: "Geri bildirim kaydedildi",
        }),
      });
      if (result.suppressed) {
        setDrawer(null);
        load();
      }
    } else {
      notification.error({
        message: result?.error || "Feedback failed",
      });
    }
  };

  const pages = useMemo(() => {
    if (pageCount <= 7) {
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    }
    const out = new Set([1, pageCount, page, page - 1, page + 1]);
    return [...out].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
  }, [page, pageCount]);

  const rangeStart = filtered === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filtered);

  return (
    <div className={styles.page}>
      <DashboardPageChrome>
        <DashboardHeader
          shared={shared}
          topicsType="topic-gap"
          user={reduxUser || user}
          dashboardId={id}
        />
        <NestedTabs />
      </DashboardPageChrome>
      <div className={styles.body}>
        <div className={styles.content}>
          <h1>
            {t("dashboard.topic_gap_title", {
              defaultValue: "Keşfedilen konseptler",
            })}
          </h1>
          <p className={styles.sub}>
            {t("dashboard.topic_gap_sub", {
              defaultValue:
                "Kalıcı defter · silince yorumlar ve geri bildirim CASCADE silinir · sayfa başına 10",
            })}
          </p>
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>
                {t("dashboard.topic_gap_ledger", { defaultValue: "Defter" })}
              </div>
              <div className={styles.kpiValue}>{total.toLocaleString()}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>
                {t("dashboard.topic_gap_filter_kpi", { defaultValue: "Filtre" })}
              </div>
              <div className={styles.kpiValue}>{filtered.toLocaleString()}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>
                {t("dashboard.topic_gap_selected", { defaultValue: "Seçili" })}
              </div>
              <div className={styles.kpiValue}>{selected.size}</div>
            </div>
          </div>
          <div className={styles.rowbar}>
            <div className={styles.filters}>
              <span className={styles.fl}>
                {t("dashboard.topic_gap_reviews_filter", { defaultValue: "Yorum" })}
              </span>
              {SUPPORTS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.chip} ${support === key ? styles.chipOn : ""}`}
                  onClick={() => {
                    setSupport(key);
                    setPage(1);
                  }}
                >
                  {key === "5plus" ? "5+" : key}
                  {histogram[key === "5plus" ? "5+" : key] != null
                    ? ` (${histogram[key === "5plus" ? "5+" : key]})`
                    : ""}
                </button>
              ))}
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.download}
                disabled={downloading || !filtered}
                onClick={downloadCsv}
              >
                {downloading
                  ? t("dashboard.topic_gap_downloading", {
                      defaultValue: "İndiriliyor…",
                    })
                  : t("dashboard.topic_gap_download", { defaultValue: "İndir" })}
              </button>
              <button
                type="button"
                className={styles.danger}
                disabled={!selected.size || saving}
                onClick={deleteSelected}
              >
                {t("dashboard.topic_gap_delete_n", {
                  defaultValue: "Seçilen {{count}} konsepti sil",
                  count: selected.size,
                })}
              </button>
            </div>
          </div>
          {loading ? (
            <div className={styles.loading}>
              <Spin />
            </div>
          ) : (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th />
                    <th>
                      {t("dashboard.topic_gap_col_concept", {
                        defaultValue: "Konsept / neden yeni",
                      })}
                    </th>
                    <th>
                      {t("dashboard.topic_gap_col_reviews", { defaultValue: "Yorum" })}
                    </th>
                    <th>
                      {t("dashboard.topic_gap_col_discovery", {
                        defaultValue: "Keşif run",
                      })}
                    </th>
                    <th>
                      {t("dashboard.topic_gap_col_updated", {
                        defaultValue: "Son güncelleme",
                      })}
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {concepts.length === 0 && (
                    <tr>
                      <td colSpan={6} className={styles.empty}>
                        {t("dashboard.topic_gap_empty", {
                          defaultValue: "Bu filtrede konsept yok",
                        })}
                      </td>
                    </tr>
                  )}
                  {concepts.map((row) => (
                    <tr
                      key={row.concept_id}
                      className={selected.has(row.concept_id) ? styles.sel : ""}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(row.concept_id)}
                          onChange={() => toggle(row.concept_id)}
                        />
                      </td>
                      <td>
                        <div className={styles.name}>{row.canonical_form}</div>
                        <div className={styles.why}>
                          {whyText(row.coverage_reason, t)}
                        </div>
                      </td>
                      <td>{row.unique_review_count}</td>
                      <td className={styles.dates}>
                        {fmtDay(row.first_seen_run_at || row.first_seen_at)}
                        <br />
                        <span>
                          {row.calendar_day_count
                            ? `${row.calendar_day_count} gün`
                            : ""}
                        </span>
                      </td>
                      <td className={styles.dates}>
                        {fmtDay(row.last_processed_run_at || row.last_seen_at)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`${styles.btn} ${
                            drawer?.concept_id === row.concept_id ? styles.btnPri : ""
                          }`}
                          onClick={() => openDrawer(row)}
                        >
                          {t("dashboard.topic_gap_reviews_btn", {
                            defaultValue: "Yorumlar",
                          })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={styles.pager}>
                <span>
                  {rangeStart}–{rangeEnd} / {filtered.toLocaleString()} ·{" "}
                  {t("dashboard.topic_gap_per_page", {
                    defaultValue: "sayfa başına 10",
                  })}
                </span>
                <div className={styles.pages}>
                  {pages.map((n, idx) => {
                    const prev = pages[idx - 1];
                    return (
                      <React.Fragment key={n}>
                        {prev && n - prev > 1 ? (
                          <span className={styles.pg}>…</span>
                        ) : null}
                        <button
                          type="button"
                          className={`${styles.pg} ${n === page ? styles.pgOn : ""}`}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        {drawer && (
          <aside className={styles.drawer}>
            <h2>{drawer.canonical_form}</h2>
            <div className={styles.meta}>
              {t("dashboard.topic_gap_discovery", { defaultValue: "Keşif" })}:{" "}
              {fmtDay(drawer.first_seen_run_at || drawer.first_seen_at)} ·{" "}
              {t("dashboard.topic_gap_col_updated", {
                defaultValue: "Son güncelleme",
              })}
              : {fmtDay(drawer.last_processed_run_at || drawer.last_seen_at)}
              <br />
              {drawer.unique_review_count}{" "}
              {t("dashboard.topic_gap_unique_reviews", {
                defaultValue: "benzersiz yorum",
              })}{" "}
              · {drawer.calendar_day_count}{" "}
              {t("dashboard.topic_gap_days", { defaultValue: "takvim günü" })} ·{" "}
              {drawer.coverage_state}
            </div>
            <div className={styles.algo}>
              <b>
                {t("dashboard.topic_gap_algo", {
                  defaultValue: "Algoritma — neden yeni konsept?",
                })}
              </b>
              {whyText(drawer.coverage_reason, t)}
            </div>
            <label>
              {t("dashboard.topic_gap_associated", {
                defaultValue: "Associated reviews",
              })}
            </label>
            {reviewsLoading ? (
              <Spin />
            ) : (
              reviews.map((rev) => (
                <div key={`${rev.review_id}-${rev.evidence_span}`} className={styles.rev}>
                  <div className={styles.rid}>
                    {rev.date_gpid || rev.review_id}
                  </div>
                  {rev.evidence_span}
                </div>
              ))
            )}
            <label>
              {t("dashboard.topic_gap_feedback", { defaultValue: "Geri bildirim" })}
            </label>
            {FEEDBACK_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`${styles.opt} ${
                  feedbackLabel === opt.id ? styles.optOn : ""
                }`}
                onClick={() => setFeedbackLabel(opt.id)}
              >
                {t(`dashboard.${opt.key}`, { defaultValue: opt.id })}
              </button>
            ))}
            <textarea
              value={feedbackReason}
              onChange={(e) => setFeedbackReason(e.target.value)}
              placeholder={t("dashboard.topic_gap_why", { defaultValue: "Neden?" })}
            />
            <button
              type="button"
              className={styles.save}
              disabled={saving}
              onClick={saveFeedback}
            >
              {t("dashboard.topic_gap_save_feedback", {
                defaultValue: "Geri bildirimi kaydet",
              })}
            </button>
          </aside>
        )}
      </div>
    </div>
  );
};

export default TopicGapConcepts;
